import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import {
    AddUpdateBestPracticeResponse,
    BestPracticeData,
    BestPracticeDataResponse,
    RemoveBestPracticeResponse,
} from '@deltek/specification-client/core/Models/bestPractice'
import {
    ContentLibrary,
    ListingLevel,
    ManufacturerProductBimLinkDetail,
    ManufacturerProductCatalogDetail,
    ManufacturerProductImageDetail,
    ManufacturerProductSpecDocDetail,
    ManufacturerProductVideoDetail,
} from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import { BestPracticesSetting } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
import {
    compareBestPracticesMutation,
    getIsBP,
} from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { ProductListingCardLogo } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardLogo'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Grid from '@material-ui/core/Grid'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import MoreVert from '@material-ui/icons/MoreVert'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { reactiveWorkspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { format } from 'date-fns'

import { NO_PRODUCT_NAME, UNKNOWN_PRODUCT_NAME } from 'pages/components/constants'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import Truncate from 'react-truncate'
import { useIsBestPracticeTagDisabled } from 'shared/utils/bestPracticeUtils'
import {
    getFromLocalStorage,
    WorkspaceManufacturerListingItem,
} from 'shared/utils/workspaceUtils'
import { DialogContext } from '../DialogContext'
import {
    ProductListingDialogFacet,
    ProductListingSelected,
} from '../ProductListingDialog.types'
import {
    ProductListingCardAction,
    ProductListingCardActionType,
} from './ProductListingCardAction'
import ProductListingCardBadge from './ProductListingCardBadge'
import ProductListingCardCarousel from './ProductListingCardCarousel'
import { ProductListingCardFooter } from './ProductListingCardFooter'
import { ProductListingCardPopperContainer } from './ProductListingCardPopperContainer'
import { ProductListingIconType } from './ProductListingIcon'

interface ProductCardStyleProps {
    isAEAllProductsToggled?: boolean
    isFooterColorVisible?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    cardContainer: {
        width: 'calc(100% - 4px)', // 4px is the column separator
        height: 'calc(100% - 4px)', // 4px is the row separator
        border: 'solid 1px #AAAAAA',
        '&:hover': {
            boxShadow: '0px 0px 10px #B9EAE0',
        },
        position: 'relative',
    },
    selectedCard: {
        border: '4px solid #10B091',
        boxShadow: '0px 0px 10px #B9EAE0',
    },
    multiSelectedCards: {
        border: '4px solid #0098D6',
        boxShadow: '0px 0px 10px #B9EAE0',
    },
    uppercaseText: {
        textTransform: 'uppercase',
    },
    customCardHeader: {
        height: 60,
        maxHeight: 60,
        padding: theme.spacing(0, 1.2),
    },
    mediumWeight: {
        fontWeight: 500,
    },
    cardContent: {
        height: 'calc(100% - 220px)',
    },
    productLink: {
        textDecoration: 'none',
        height: '100%',
    },
    guttered: {
        padding: theme.spacing(1.2),
    },
    grayText: {
        color: '#5E5F61',
    },
    alignText: {
        marginTop: theme.spacing(3),
    },
    cardFooter: {
        backgroundColor: '#5E5F61',
        height: 40,
    },
    publishStatusDateText: {
        fontSize: pxToRem(10),
        lineHeight: '15px',
        paddingTop: theme.spacing(0.375),
    },
    fullHeight: {
        height: '100%',
    },
    cardTypeContainer: {
        position: 'relative',
        height: pxToRem(60),
        paddingLeft: theme.spacing(1.2),
        marginLeft: theme.spacing(1.2),
    },
    BPBorder: {
        borderLeft: '2px solid #CCCCCC',
    },
    smallerLineHeight: {
        lineHeight: '20px',
    },
    customCaptionSized: {
        fontSize: pxToRem(12),
    },
    fullWidth: {
        width: '100%',
    },
    noImage: {
        textDecoration: 'none',
        textAlign: 'center',
        height: 130,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    darkGrayBackground: {
        backgroundColor: '#B1B0B1',
    },
    lightGrayBackground: {
        backgroundColor: '#c9c9c9',
    },
    largeFont: {
        fontSize: pxToRem(30),
        color: '#fff',
        fontWeight: 500,
    },
    textStyle: {
        textTransform: 'uppercase',
    },
    smallFont: {
        fontSize: pxToRem(20),
        color: '#fff',
        fontWeight: 500,
    },
    premiumNoImageTextContainer: {
        maxWidth: 308,
        fontWeight: 400,
        fontSize: pxToRem(36),
        lineHeight: '42px',
    },
    firmLogoImage: {
        height: 40,
        objectFit: 'contain',
        display: 'block',
        maxWidth: '100%',
    },
    companyLink: {
        display: 'block',
        textDecoration: 'none',
        color: '#000',
    },
    cardContentItem: {
        paddingTop: theme.spacing(0.35),
        paddingBottom: theme.spacing(0.35),
        display: 'flex',
        alignItems: 'center',
    },
    badgeContainer: {
        width: 36,
        height: 36,
        borderRadius: 360,
        backgroundColor: '#24CEAD',
        right: 5,
    },
    badgeRootContainer: {
        width: 'calc(100%)',
        height: 'calc(100%)',
    },
    popperText: {
        color: '#A78966',
        border: '1px solid #F2F2F2',
    },
    popperIcon: {
        fontSize: pxToRem(16),
        color: '#A78966',
    },
    proprietaryLabel: {
        height: 22,
        width: 81,
        fontSize: pxToRem(11),
        fontWeight: 500,
        marginTop: theme.spacing(1.5),
        position: 'absolute',
        backgroundColor: '#FD3431',
        color: '#FFFFFF',
        padding: '3px 11px',
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2,
    },
    popperContainer: { width: 150, height: 35, backgroundColor: '#FFFFFF' },
    popperSeparator: { borderRight: '1px solid #F2F2F2' },
    actionContainer: {
        cursor: (props: ProductCardStyleProps) =>
            !props.isAEAllProductsToggled ? 'pointer' : 'default',
        display: 'flex',
    },
    bestPractice: {
        position: 'absolute',
        bottom: '0',
        right: '0',
        height: 40,
        fontSize: pxToRem(24),
        color: (props: ProductCardStyleProps) =>
            props.isFooterColorVisible ? '#FFFFFF' : '#5E5F61',
        display: 'flex',
        alignItems: 'center',
    },
    rightContainer: {
        display: 'flex',
    },
    BPIconContainer: {
        padding: theme.spacing(1.25, 0),
    },
    BPIconColor: {
        color: '#10B091',
        fontSize: pxToRem(30),
        textAlign: 'right',
    },
}))

export const removeFamilyNumberFromFamilyNameRegex = new RegExp(
    '(\\d+(\\.\\d+)*\\s-\\s)',
    'g'
)

export interface ProductCardProps {
    firmName?: string
    firmLogoUrl?: string
    productName?: string
    hideProductName?: boolean
    productType?: string
    listingLevel?: ListingLevel
    submissionStatus?: string
    publishDate?: string
    isActive?: boolean
    familyName?: string
    listingId?: string
    categories?: string[]
    firmId?: string
    specAgentProductTypeId?: number
    images?: Partial<ManufacturerProductImageDetail>[]
    videos?: Partial<ManufacturerProductVideoDetail>[]
    catalogs?: Partial<ManufacturerProductCatalogDetail>[]
    manufacturerDocs?: Partial<ManufacturerProductSpecDocDetail>[]
    bimLinks?: Partial<ManufacturerProductBimLinkDetail>[]
    loading?: boolean
    isProductBOD?: boolean
    isAssembly: boolean
    cardAction: ProductListingCardActionType
    cardClick?: (value?: number) => void
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isProductDeprecated?: boolean
    hasManufacturerOptions?: boolean
    hasSupportedOptions?: boolean
    isListingManufacturerProductType?: boolean
    contentLibrary?: ContentLibrary
}

export interface ListingCardMedia {
    imgUrl: string
    isVideo: boolean
    order: number
}

const ProductListingCard: React.FC<ProductCardProps> = ({
    productName = '',
    listingId,
    firmName = '',
    productType = '',
    hideProductName = productName === UNKNOWN_PRODUCT_NAME || productName === NO_PRODUCT_NAME,
    listingLevel = ListingLevel.Manufacturer,
    publishDate,
    familyName,
    isActive = false,
    isAssembly = true,
    firmLogoUrl,
    categories = [],
    firmId = '',
    specAgentProductTypeId,
    images,
    videos,
    catalogs,
    manufacturerDocs,
    bimLinks,
    loading,
    isProductBOD,
    cardAction,
    cardClick,
    isBestPractice,
    isGlobalBestPractice,
    isProductDeprecated,
    hasManufacturerOptions,
    hasSupportedOptions,
    isListingManufacturerProductType,
    contentLibrary,
}) => {
    const {
        hidePopper,
        emptyElementTree,
        productSelected,
        productListingFlaggedForReview,
    } = React.useContext(DialogContext)

    const {
        currentUserInfo: { kpUserId: userId },
        isAdmin,
    } = useReactiveVar(userInfo)

    // use this flag as guard condition: don't show BP icon if project imported family, just to not confuse the user if for some reason it was tagged as BP
    const isProjectImported = reactiveWorkspaceContents()?.isProjectImported

    const isBPTagDisabledInput = {
        userInfo,
        isProjectImported,
    }

    // checking if BP tags are disabled for editing
    const isBestPracticeEditDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
    })

    // checking if BP tags are disabled for viewing
    const isBestPracticeViewDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        isViewOnly: true,
    })

    const familyNumber: string = React.useMemo(() => {
        return familyName?.split('-')?.[0].trim() ?? ''
    }, [familyName])

    const [popoverAnchor, setPopoverAnchor] = React.useState<null | Element>(
        null
    )
    // Use useMemo instead of useEffect to avoid unnecessary re-renders
    // when other products' BP status changes
    const currentProduct = React.useMemo<ProductListingSelected | undefined>(
        () => productSelected?.find((each) => each.listingId === listingId),
        [productSelected, listingId]
    )

    const [isBPMenuOpen, setIsBPMenuOpen] = React.useState<boolean>(false)
    const {
        handleUpdateWorkspaceManufacturerBP,
        projectDetail,
        setPopperListingId,
        popperListingId,
        listingId: selectedListingId,
        selectedManufacturers,
        handleMultiSelectManufacturer,
        isAEAllProductsToggled,
        listingType,
    } = React.useContext(DialogContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const areBadgelessCardsEnabled =
        processEnv.REACT_APP_EnableBadgelessCards === '1'

    const classes = useStyles({
        isAEAllProductsToggled: isAEAllProductsToggled,
        isFooterColorVisible:
            areBadgelessCardsEnabled &&
            (listingLevel === ListingLevel.Manufacturer ||
                listingLevel === ListingLevel.ProductListing),
    })

    const [
        popperAnchorEl,
        setPopperAnchorEl,
    ] = React.useState<HTMLDivElement | null>(null)

    // Track if there's an active dialog to prevent popper from being hidden
    const [hasActiveDialog, setHasActiveDialog] = React.useState<boolean>(false)

    // The element anchor of the popper is the card media which has photos and videos
    const cardMediaRef = React.useRef<HTMLDivElement | null>(null)

    const [
        getBestPracticeElementData,
        {
            error: bestPracticeElementError,
            data: bestPracticeElementData,
            loading: bestPracticeElementLoading,
        },
    ] = useLazyQuery<BestPracticeDataResponse>(BestPracticeQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'BestPracticeElement',
    })

    const getPersistedState = React.useCallback(
        (propKey: string) => {
            return getFromLocalStorage(userId, projectDetail?.id!, propKey)
        },
        [userId, projectDetail]
    )

    const [setAddUpdateBestPractice] = useMutation<
        AddUpdateBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(AddUpdateBestPracticeMutation, {
        onCompleted: (data: AddUpdateBestPracticeResponse) => {
            if (data && data.addUpdateBestPractice) {
                const isBPLocal = getIsBP(data.addUpdateBestPractice)
                const isGBPLocal = data.addUpdateBestPractice?.isGlobal
                // Update context which will automatically update currentProduct via useMemo
                handleUpdateWorkspaceManufacturerBP?.({
                    listingId,
                    isBP: isBPLocal,
                    isGBP: isGBPLocal,
                } as WorkspaceManufacturerListingItem)
            }
        },
    })

    const [setRemoveBestPractice] = useMutation<
        RemoveBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(RemoveBestPracticeMutation, {
        onCompleted: (data: RemoveBestPracticeResponse) => {
            if (data && data.removeBestPractice) {
                // Update context which will automatically update currentProduct via useMemo
                handleUpdateWorkspaceManufacturerBP?.({
                    listingId,
                    isBP: false,
                    isGBP: false,
                } as WorkspaceManufacturerListingItem)
            }
        },
    })
    const getlistingCardId = React.useCallback(() => {
        return `${listingId}_${familyNumber}`
    }, [listingId, familyNumber])

    const handleBestPracticesMutationData = React.useCallback(
        (mutationData: Partial<BestPracticeData> | null) => {
            // Always close the menu when mutation is handled
            setPopoverAnchor(null)
            setIsBPMenuOpen(false)

            if (mutationData) {
                const isMutationUpdated = compareBestPracticesMutation(
                    bestPracticeElementData,
                    projectDetail,
                    mutationData
                )
                if (isMutationUpdated) {
                    const defaultInput = {
                        ...mutationData,
                        parentProjectId: projectDetail?.id,
                        listingId: listingId,
                        familyNumber: familyNumber,
                        productType: productType,
                    }

                    defaultInput.projectElementNodeId = getPersistedState?.(
                        'selectedNodeId'
                    )

                    const mutationInput =
                        bestPracticeElementData?.getBestPractice?.id &&
                            bestPracticeElementData?.getBestPractice
                                ?.parentProjectId === projectDetail?.id
                            ? {
                                ...defaultInput,
                                id:
                                    bestPracticeElementData?.getBestPractice
                                        ?.id,
                            }
                            : defaultInput
                    setAddUpdateBestPractice({
                        variables: {
                            input: mutationInput,
                        },
                    })
                }
            } else {
                // removed the other condition to allow user to change the BP setting on different projects
                if (bestPracticeElementData?.getBestPractice?.id) {
                    setRemoveBestPractice({
                        variables: {
                            input: {
                                id:
                                    bestPracticeElementData?.getBestPractice
                                        ?.id,
                            },
                        },
                    })
                }
            }
        },
        [
            bestPracticeElementData,
            projectDetail,
            listingId,
            familyNumber,
            setAddUpdateBestPractice,
            setRemoveBestPractice,
            productType,
            getPersistedState,
        ]
    )

    const handleMouseOver = () => {
        setPopperAnchorEl(cardMediaRef?.current)
        setPopperListingId?.(listingId)
    }

    const handleMouseLeave = () => {
        // Don't hide popper if there's an active dialog
        if (!hasActiveDialog) {
            setPopperAnchorEl(null)
            setPopperListingId?.(undefined)
        }
    }

    const handleDialogStateChange = React.useCallback((hasDialog: boolean) => {
        setHasActiveDialog(hasDialog)
        // If dialog is closed and we're not hovering, hide the popper
        if (!hasDialog && !cardMediaRef.current) {
            setPopperAnchorEl(null)
            setPopperListingId?.(undefined)
        }
    }, [setPopperListingId])

    const listingPath = `/products/${listingId ? 'product/listing/' + listingId : 'all'
        }`

    const consolidatedMedia: ListingCardMedia[] = React.useMemo(() => {
        const imageContents = images
            ? images
                .filter((each) => each.filename)
                .map(
                    (imageItem: Partial<ManufacturerProductImageDetail>) => ({
                        imgUrl: suffixDimension({
                            filename: imageItem.filename!,
                            dimension: {
                                width: 400,
                                height: 300,
                            },
                        }),
                        isVideo: false,
                        order: imageItem.order ?? 0,
                    })
                )
            : []

        const videoStills = videos
            ? videos
                .filter((each) => each.videoStillImageUrl)
                .map(
                    (videoItem: Partial<ManufacturerProductVideoDetail>) => ({
                        imgUrl: videoItem.videoStillImageUrl!,
                        isVideo: true,
                        order: videoItem.order ?? 0,
                    })
                )
            : []

        return [...imageContents, ...videoStills].sort(
            (a, b) => a.order - b.order
        )
    }, [images, videos])

    // this determines if project is from SMM workspace (DraftProject)
    const isDraftProject =
        projectDetail?.number === 'DraftProject' ||
        projectDetail?.groupName === null

    const isShowProductCardPopper =
        !!listingId &&
        listingLevel !== ListingLevel.PremiumProprietary &&
        (!hidePopper || hasActiveDialog) && // Keep popper alive if there's an active dialog
        !isAssembly &&
        !emptyElementTree &&
        !isDraftProject

    const hasGBPButton = React.useMemo(
        () =>
            !!projectDetail && listingLevel !== ListingLevel.PremiumProprietary,
        [projectDetail, listingLevel]
    )

    // check if the product is added to PT
    const selected = !!currentProduct
    const isSelectedAsBOD = currentProduct?.selectionType === 'BOD'
    // check if the product is flagged for review
    const reviewItem = productListingFlaggedForReview?.find(
        (each) => each.listingId === listingId
    )
    const isFlaggedForReview: boolean = !!reviewItem
    const productListingIconType:
        | ProductListingIconType
        | undefined = !!selected
            ? isSelectedAsBOD
                ? ProductListingIconType.BOD
                : ProductListingIconType.ADD
            : isFlaggedForReview
                ? ProductListingIconType.FLAGFORREVIEW
                : // not selected or flagged for review
                undefined

    // checking the selected items to see if the BP tags are updated locally
    const isBP = React.useMemo(
        () => currentProduct?.isBestPractice ?? isBestPractice,
        [currentProduct, isBestPractice]
    )
    const isGBP = React.useMemo(
        () =>
            !isBP
                ? currentProduct?.isGlobalBestPractice ?? isGlobalBestPractice
                : false,
        [currentProduct, isGlobalBestPractice, isBP]
    )
    const {
        getFacetSelectedOptions,
        setIsPTNotSelectedInFilter,
        setIsFamilyNotSelectedInFilter,
    } = React.useContext(DialogContext)

    const getValue = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getFacetSelectedOptions(value)
        },
        [getFacetSelectedOptions]
    )

    const getErrorMessage = React.useCallback(
        (productTypeInSearch, familyInSearch) => {
            switch (true) {
                // When both Family and PT is not selected in Product filter
                case (!productTypeInSearch ||
                    productTypeInSearch.length === 0) &&
                    (!familyInSearch || familyInSearch.length === 0):
                    setIsPTNotSelectedInFilter?.(true)
                    setIsFamilyNotSelectedInFilter?.(true)
                    return 'Please select a Family and a PT to attach BP tags'

                // When PT is not selected in Product filter
                case !productTypeInSearch || productTypeInSearch.length === 0:
                    setIsPTNotSelectedInFilter?.(true)
                    return 'Please select a PT to attach BP tags'

                // When Family is not selected in Product filter
                case !familyInSearch || familyInSearch.length === 0:
                    setIsFamilyNotSelectedInFilter?.(true)
                    return 'Please select a Family to attach BP tags'
                default:
                    return ''
            }
        },
        [setIsPTNotSelectedInFilter, setIsFamilyNotSelectedInFilter]
    )

    // check if a PT and Family is selected in filter
    const checkIfRequiredFieldsSelected = React.useCallback(() => {
        const productTypeInSearch = getValue('ProductType') // getting selected PT value from filter
        const familyInSearch = getValue('SectionNames') // getting selected Family value from filter
        if (
            !productTypeInSearch ||
            productTypeInSearch.length === 0 ||
            !familyInSearch ||
            familyInSearch.length === 0
        ) {
            showGlobalSnackbar({
                variables: {
                    message: getErrorMessage(
                        productTypeInSearch,
                        familyInSearch
                    ),
                    variant: 'error',
                    id: `ProductListingsPage_BPTagsError`,
                },
            })
            return
        }
    }, [getValue, showGlobalSnackbar])

    const handleOpenBPMenu = React.useCallback(
        (event) => {
            event.preventDefault()
            event.stopPropagation()

            // Check required fields first
            checkIfRequiredFieldsSelected()

            // Toggle menu if clicking same element, otherwise open
            if (popoverAnchor === event.currentTarget) {
                setPopoverAnchor(null)
                setIsBPMenuOpen(false)
            } else {
                setIsBPMenuOpen(true)
                setPopoverAnchor(event.currentTarget)
                getBestPracticeElementData({
                    variables: {
                        parentId: getlistingCardId(),
                        projectId: projectDetail?.id,
                    },
                })
            }
        },
        [
            checkIfRequiredFieldsSelected,
            getBestPracticeElementData,
            getlistingCardId,
            projectDetail,
            popoverAnchor,
        ]
    )

    // Helper function to check if Best Practice menu should be shown
    const canShowBestPracticeMenu = React.useCallback(() => {
        return (
            (selected || isSelectedAsBOD) &&
            !isBestPracticeEditDisabled &&
            listingId &&
            !loading &&
            projectDetail &&
            listingLevel !== ListingLevel.PremiumProprietary
        )
    }, [selected, isSelectedAsBOD, isBestPracticeEditDisabled, listingId, loading, projectDetail, listingLevel])

    // Helper function to validate required selections
    const hasRequiredSelections = React.useCallback(() => {
        const productTypeInSearch = getValue('ProductType')
        const familyInSearch = getValue('SectionNames')
        return productTypeInSearch?.length > 0 && familyInSearch?.length > 0
    }, [getValue])

    // Helper function to toggle Best Practice menu
    const toggleBestPracticeMenu = React.useCallback((event: React.MouseEvent) => {
        if (popoverAnchor === event.currentTarget) {
            setPopoverAnchor(null)
            setIsBPMenuOpen(false)
        } else {
            setIsBPMenuOpen(true)
            setPopoverAnchor(event.currentTarget)
            getBestPracticeElementData({
                variables: {
                    parentId: getlistingCardId(),
                    projectId: projectDetail?.id,
                },
            })
        }
    }, [popoverAnchor, getBestPracticeElementData, getlistingCardId, projectDetail])

    const handleRightClick = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.preventDefault()
            event.stopPropagation()

            if (!canShowBestPracticeMenu()) {
                return
            }

            const productTypeInSearch = getValue('ProductType')
            const familyInSearch = getValue('SectionNames')

            if (hasRequiredSelections()) {
                checkIfRequiredFieldsSelected()
                toggleBestPracticeMenu(event)
            } else {
                // Show error message about missing selections
                showGlobalSnackbar({
                    variables: {
                        message: getErrorMessage(productTypeInSearch, familyInSearch),
                        variant: 'error',
                        id: `ProductListingsPage_BPTagsError`,
                    },
                })
            }
        },
        [
            canShowBestPracticeMenu,
            hasRequiredSelections,
            getValue,
            checkIfRequiredFieldsSelected,
            toggleBestPracticeMenu,
            showGlobalSnackbar,
            getErrorMessage,
        ]
    )

    // Add check to prevent hiding of popper
    React.useEffect(() => {
        if (!!popperListingId && popperListingId === listingId) {
            setPopperAnchorEl(cardMediaRef?.current)
        } else {
            setPopperAnchorEl(null)
        }
    }, [popperListingId, listingId, cardMediaRef])

    const isAction = cardAction === 'Action'
    const handleCardClick = isAction
        ? (event?: React.MouseEvent) => {
            const isFeatureEnabled = processEnv.REACT_APP_EnableDynamicManufacturerListSelection === '1'
            const isManufacturerFilterActive = listingType?.includes('Manufacturer')
            const isCtrlClick = event?.ctrlKey
            
            if (isCtrlClick && isFeatureEnabled && isManufacturerFilterActive) {
                // Ctrl+click multi-selection
                event?.preventDefault()
                event?.stopPropagation()
                handleMultiSelectManufacturer?.(
                    listingId!,
                    listingLevel,
                    firmName,
                    productType,
                    specAgentProductTypeId
                )
            } else if (!isCtrlClick) {
                // Normal click opens card
                cardClick?.()
            }
            // else: Ctrl+click without proper conditions → do nothing
        }
        : undefined

    return (
        <ProductListingCardBadge
            loading={loading}
            listingId={listingId}
            productListingIconType={productListingIconType}
        >
            <Card
                variant="elevation"
                square
                elevation={2}
                className={clsx(
                    'productListingCardItem',
                    classes.cardContainer,
                    listingId &&
                    listingId !== '' &&
                    listingId === selectedListingId &&
                    classes.selectedCard,
                    listingId &&
                    selectedManufacturers?.some(
                        (item) => item.listingId === listingId
                    ) &&
                    classes.multiSelectedCards
                )}
                data-testid={`productListingCardItem${listingId}`}
                onContextMenu={handleRightClick}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseOver}
            >
                <GridContainer
                    justifyContent="space-between"
                    alignItems="center"
                    className={clsx(
                        classes.customCardHeader,
                        isAction && classes.actionContainer
                    )}
                    onClick={handleCardClick}
                    onKeyDown={onEnterKeyDown(handleCardClick)}
                    tabIndex={0}
                >
                    <GridItem xs={5}>
                        <ProductListingCardAction
                            type={cardAction}
                            target="_blank"
                            to={`/products/company/${firmId}`}
                            linkClassName={classes.companyLink}
                        >
                            {loading && (
                                <Skeleton
                                    variant="rect"
                                    animation="pulse"
                                    height={40}
                                    width={110}
                                    className="loadingSkeleton"
                                />
                            )}
                            {!loading && (
                                <>
                                    {firmLogoUrl ? (
                                        <img
                                            src={firmLogoUrl}
                                            className={classes.firmLogoImage}
                                            alt={firmName}
                                            title={firmName}
                                        />
                                    ) : (
                                        <Truncate lines={2}>
                                            {firmName}
                                        </Truncate>
                                    )}
                                </>
                            )}
                        </ProductListingCardAction>
                    </GridItem>
                    <GridItem className={classes.rightContainer}>
                        {!isBestPracticeViewDisabled && (isBP || isGBP) && (
                            <GridItem
                                xs={2}
                                className={classes.BPIconContainer}
                            >
                                <div className={classes.BPIconColor}>
                                    <BestPracticesIcons
                                        isGlobalBestPractice={isGBP}
                                        isBestPractice={isBP}
                                    />
                                </div>
                            </GridItem>
                        )}
                        <GridContainer
                            className={clsx(
                                classes.fullHeight,
                                classes.cardTypeContainer,
                                {
                                    [classes.BPBorder]:
                                        !isBestPracticeViewDisabled &&
                                        (isBP || isGBP),
                                }
                            )}
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                        >
                            {!areBadgelessCardsEnabled &&
                                listingLevel !== ListingLevel.Manufacturer && (
                                    <ProductListingCardLogo
                                        listingLevel={listingLevel}
                                        loading={loading}
                                    />
                                )}
                            <GridItem>
                                <Typography
                                    component="div"
                                    variant="caption"
                                    className={clsx(
                                        classes.publishStatusDateText,
                                        classes.grayText,
                                        listingLevel ===
                                        ListingLevel.Manufacturer &&
                                        classes.alignText
                                    )}
                                >
                                    {!loading ? (
                                        <>
                                            {publishDate
                                                ? `Published ${format(
                                                    new Date(publishDate),
                                                    'MM/dd/yyyy'
                                                )} `
                                                : 'Published '}
                                            - {isActive ? 'Active' : 'Inactive'}
                                        </>
                                    ) : (
                                        <Skeleton
                                            variant="text"
                                            animation="pulse"
                                            width={100}
                                            className="loadingSkeleton"
                                        />
                                    )}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
                <CardMedia ref={cardMediaRef}>
                    {loading ? (
                        <Skeleton
                            variant="rect"
                            animation="pulse"
                            height={130}
                        />
                    ) : (
                        <>
                            {!areBadgelessCardsEnabled &&
                                listingLevel ===
                                ListingLevel.PremiumProprietary && (
                                    <span className={classes.proprietaryLabel}>
                                        Proprietary
                                    </span>
                                )}
                            {listingLevel === ListingLevel.Manufacturer ? (
                                <ProductListingCardAction
                                    type={cardAction}
                                    to={listingPath}
                                    linkClassName={clsx(
                                        classes.noImage,
                                        classes.darkGrayBackground
                                    )}
                                    actionClassName={clsx(
                                        classes.noImage,
                                        classes.darkGrayBackground
                                    )}
                                    onClick={handleCardClick}
                                    unselectable="on"
                                >
                                    <Typography
                                        className={clsx(
                                            classes.largeFont,
                                            'productListingCardBasicListingPreviewText'
                                        )}
                                    >
                                        {(contentLibrary ===
                                            ContentLibrary.MasterSpec ||
                                            !!!Number(
                                                processEnv.REACT_APP_ShowContentLibraryOptions
                                            )) && (
                                                <Typography
                                                    className={clsx(
                                                        classes.smallFont
                                                    )}
                                                >
                                                    MasterSpec Approved
                                                </Typography>
                                            )}
                                        <Typography
                                            className={clsx(
                                                classes.largeFont,
                                                classes.textStyle
                                            )}
                                        >
                                            {listingLevel}
                                        </Typography>
                                    </Typography>
                                </ProductListingCardAction>
                            ) : consolidatedMedia.length > 0 ? (
                                <ProductListingCardCarousel
                                    media={consolidatedMedia}
                                    redirectPath={listingPath}
                                    redirectType={cardAction}
                                    redirectAction={cardClick}
                                />
                            ) : (
                                <ProductListingCardAction
                                    type={cardAction}
                                    to={listingPath}
                                    linkClassName={clsx(
                                        classes.noImage,
                                        classes.lightGrayBackground
                                    )}
                                    actionClassName={clsx(
                                        classes.noImage,
                                        classes.lightGrayBackground
                                    )}
                                    onClick={() => cardClick?.()}
                                    unselectable="on"
                                >
                                    {!(
                                        areBadgelessCardsEnabled &&
                                        (listingLevel ===
                                            ListingLevel.Premium ||
                                            listingLevel ===
                                            ListingLevel.PremiumMasterSpec ||
                                            listingLevel ===
                                            ListingLevel.PremiumProprietary)
                                    ) && (
                                            <Typography
                                                className={clsx(
                                                    classes.largeFont,
                                                    listingLevel.includes(
                                                        'Premium'
                                                    ) &&
                                                    classes.premiumNoImageTextContainer,
                                                    'productListingCardNoImageText'
                                                )}
                                            >
                                                {listingLevel &&
                                                    (listingLevel.includes('Premium') ||
                                                        listingLevel.includes(
                                                            'Product Listing'
                                                        ))
                                                    ? listingLevel
                                                    : listingLevel + ' Listing'}
                                            </Typography>
                                        )}
                                </ProductListingCardAction>
                            )}
                        </>
                    )}
                </CardMedia>
                {isShowProductCardPopper && (
                    <ProductListingCardPopperContainer
                        listingId={listingId ?? ''}
                        listingLevel={listingLevel ?? ''}
                        productType={productType ?? ''}
                        isProductBOD={isProductBOD}
                        popperAnchorEl={popperAnchorEl}
                        isProductDeprecated={isProductDeprecated}
                        hasManufacturerOptions={hasManufacturerOptions}
                        hasSupportedOptions={hasSupportedOptions}
                        isListingManufacturerProductType={
                            isListingManufacturerProductType
                        }
                        isFlaggedForReview={isFlaggedForReview}
                        onDialogStateChange={handleDialogStateChange}
                    />
                )}
                <CardContent
                    component={Grid}
                    container
                    direction="column"
                    spacing={1}
                    className={clsx(
                        'cardBodyContainer',
                        classes.guttered,
                        classes.cardContent,
                        cardAction === 'Action' && classes.actionContainer
                    )}
                    onClick={
                        cardAction === 'Action'
                            ? handleCardClick
                            : undefined
                    }
                >
                    <ProductListingCardAction
                        type={cardAction}
                        to={listingPath}
                        linkClassName={classes.productLink}
                    >
                        {!loading ? (
                            <>
                                {categories?.length > 0 && (
                                    <GridItem>
                                        <Typography
                                            className={clsx(
                                                classes.customCaptionSized,
                                                classes.grayText,
                                                classes.cardContentItem
                                            )}
                                        >
                                            <Truncate lines={2}>
                                                {categories
                                                    .join(', ')
                                                    .toUpperCase()}
                                            </Truncate>
                                        </Typography>
                                    </GridItem>
                                )}
                                {productName && !hideProductName && (
                                    <GridItem>
                                        <Typography
                                            variant="h6"
                                            className={clsx(
                                                'productListingCardProductName',
                                                classes.mediumWeight,
                                                classes.smallerLineHeight,
                                                classes.grayText,
                                                classes.cardContentItem
                                            )}
                                        >
                                            <Truncate lines={2}>
                                                {productName}
                                            </Truncate>
                                        </Typography>
                                    </GridItem>
                                )}
                                {familyName && (
                                    <GridItem>
                                        <Typography
                                            className={clsx(
                                                classes.customCaptionSized,
                                                classes.mediumWeight,
                                                classes.grayText,
                                                classes.cardContentItem
                                            )}
                                        >
                                            <HoverTooltip
                                                text={familyName}
                                                children={
                                                    <Truncate lines={2}>
                                                        {familyName
                                                            .replace(
                                                                removeFamilyNumberFromFamilyNameRegex,
                                                                ''
                                                            )
                                                            .toUpperCase()}
                                                    </Truncate>
                                                }
                                            />
                                        </Typography>
                                    </GridItem>
                                )}
                                <GridItem className={classes.fullWidth}>
                                    <Typography
                                        className={clsx(
                                            classes.customCaptionSized,
                                            classes.grayText,
                                            classes.cardContentItem
                                        )}
                                    >
                                        <Truncate lines={2}>
                                            {productType}
                                        </Truncate>
                                    </Typography>
                                </GridItem>
                            </>
                        ) : (
                            <>
                                {/* categories */}
                                <Skeleton
                                    variant="text"
                                    animation="pulse"
                                    width={100}
                                    className="loadingSkeleton"
                                />

                                {/* Product Name */}
                                <Skeleton
                                    variant="text"
                                    animation="pulse"
                                    width={70}
                                    className="loadingSkeleton"
                                />

                                {/* Family Name */}
                                <Skeleton
                                    variant="text"
                                    animation="pulse"
                                    width={150}
                                    className="loadingSkeleton"
                                />

                                {/* Product Type */}
                                <Skeleton
                                    variant="rect"
                                    animation="pulse"
                                    height={20}
                                    width={200}
                                    className="loadingSkeleton"
                                />
                            </>
                        )}
                    </ProductListingCardAction>
                </CardContent>
                <GridItem
                    className={
                        (cardAction === 'Action' && classes.actionContainer) ||
                        ''
                    }
                >
                    <ProductListingCardFooter
                        listingLevel={listingLevel}
                        loading={loading}
                        firmName={firmName}
                        familyName={familyName}
                        catalogs={catalogs}
                        bimLinks={bimLinks}
                        cardAction={cardAction}
                        manufacturerDocs={manufacturerDocs}
                        cardClick={(event) => handleCardClick?.(event)}
                        showManufacturerOptionItem={
                            [
                                ListingLevel.Premium,
                                ListingLevel.PremiumMasterSpec,
                            ].includes(listingLevel) && hasManufacturerOptions
                        }
                        hasGBPButton={hasGBPButton}
                    />
                    {!isBestPracticeEditDisabled &&
                        !loading &&
                        !!projectDetail &&
                        listingLevel !== ListingLevel.PremiumProprietary &&
                        (selected || isSelectedAsBOD) && (
                            <GridItem
                                onClick={handleOpenBPMenu}
                                onKeyDown={onEnterKeyDown(handleOpenBPMenu)}
                                tabIndex={0}
                                className={classes.bestPractice}
                            >
                                <MoreVert
                                    fontSize="inherit"
                                    data-testid="moreMenu"
                                />
                            </GridItem>
                        )}
                </GridItem>
                {!isBestPracticeEditDisabled && (
                    <BestPracticesSetting
                        onlyPopover={true}
                        customClassName={classes.bestPractice}
                        popoverAnchor={popoverAnchor}
                        projectDetail={projectDetail}
                        setBestPracticesMutationData={
                            handleBestPracticesMutationData
                        }
                        data={bestPracticeElementData}
                        loading={bestPracticeElementLoading}
                        showBestPracticesIcons={false}
                        isBestPractice={isBP}
                        isGlobalBestPractice={isGBP}
                        isDisabled={isBestPracticeEditDisabled}
                    />
                )}
            </Card>
        </ProductListingCardBadge>
    )
}

export default ProductListingCard
